import { logger } from "../services/logger.ts";
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, X, ChevronDown } from 'lucide-react';
import { Market, MarketStatus, MarketCategory } from "../types.ts";
import { MarketEngine } from "../lib/marketEngine.ts";
import { useAuth } from "../lib/auth.tsx";
import { MarketCard } from "../components/MarketCard.tsx";
import { Category as CategoryType, fetchMarketCategories } from "../services/categoryService.ts";

const marketEngine = new MarketEngine();

// Status types that can be used in filters
// Includes all official MarketStatus plus 'all'
// Removed non-enum values like 'unresolved', 'unverified', 'private', 'public', 'verified'
type FilterStatusType = MarketStatus | 'all';

export function AdminMarkets() {
  const [markets, setMarkets] = useState<Market[]>([]);
  const [filteredMarkets, setFilteredMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<FilterStatusType>('active');
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<MarketCategory | 'all'>('all');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const categoryDropdownRef = React.useRef<HTMLDivElement | null>(null);
  const categoryButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Update dropdown position when button is clicked
  const updateDropdownPosition = () => {
    if (categoryButtonRef.current) {
      const rect = categoryButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        categoryDropdownRef.current && 
        !categoryDropdownRef.current.contains(event.target as Node) &&
        categoryButtonRef.current &&
        !categoryButtonRef.current.contains(event.target as Node)
      ) {
        setShowCategoryDropdown(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      const fetchedCategories = await fetchMarketCategories();
      setCategories(fetchedCategories);
    };
    loadCategories();
  }, []);

  // Fetch markets from the database
  const fetchMarkets = async () => {
    try {
      logger.info('Fetching all markets...');
      setLoading(true);
      
      // Check if user is admin
      if (!user || user.app_metadata?.role !== 'admin') {
        navigate('/markets');
        return;
      }
      
      // Get all markets which already includes market_options and market_pools
      const validMarkets = await marketEngine.getAllMarkets();
      
      logger.info(`Loaded ${validMarkets.length} markets`);
      setMarkets(validMarkets);
      setFilteredMarkets(validMarkets);
      setError(null);
    } catch (error) {
      logger.error('Error fetching markets:', error);
      setError('Failed to load markets. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Store filters in localStorage when they change
  useEffect(() => {
    localStorage.setItem('adminMarketsStatusFilter', statusFilter);
  }, [statusFilter]);
  
  useEffect(() => {
    localStorage.setItem('adminMarketsCategoryFilter', categoryFilter);
  }, [categoryFilter]);
  
  useEffect(() => {
    localStorage.setItem('adminMarketsSearchTerm', searchTerm);
  }, [searchTerm]);

  // Fetch markets on mount and when refresh param changes
  useEffect(() => {
    fetchMarkets();
    
    // Check URL for refresh parameter and filter parameters
    const url = new URL(window.location.href);
    const refreshParam = url.searchParams.get('refresh');
    const statusParam = url.searchParams.get('status') as FilterStatusType | null;
    const categoryParam = url.searchParams.get('category') as MarketCategory | 'all' | null;
    const searchParam = url.searchParams.get('search');
    
    // Apply filters from URL parameters if present
    if (statusParam) {
      setStatusFilter(statusParam);
    } else {
      // If no URL param, try from localStorage
      const storedStatusFilter = localStorage.getItem('adminMarketsStatusFilter');
      if (storedStatusFilter) {
        setStatusFilter(storedStatusFilter as FilterStatusType);
      }
    }
    
    if (categoryParam) {
      setCategoryFilter(categoryParam);
    } else {
      // If no URL param, try from localStorage
      const storedCategoryFilter = localStorage.getItem('adminMarketsCategoryFilter');
      if (storedCategoryFilter) {
        setCategoryFilter(storedCategoryFilter as MarketCategory | 'all');
      }
    }
    
    if (searchParam) {
      setSearchTerm(searchParam);
    } else {
      // If no URL param, try from localStorage
      const storedSearchTerm = localStorage.getItem('adminMarketsSearchTerm');
      if (storedSearchTerm) {
        setSearchTerm(storedSearchTerm);
      }
    }
    
    if (refreshParam) {
      // Remove the refresh parameter from the URL without reloading the page
      url.searchParams.delete('refresh');
      window.history.replaceState({}, '', url.toString());
      
      // Force a fresh fetch of markets
      logger.info('Refresh parameter detected, forcing fresh fetch of markets');
      fetchMarkets();
    }
  }, [user, navigate]);

  // Add a listener for URL changes to handle refresh parameter
  useEffect(() => {
    const handleUrlChange = () => {
      const url = new URL(window.location.href);
      const refreshParam = url.searchParams.get('refresh');
      if (refreshParam) {
        logger.info('URL changed with refresh parameter, fetching markets');
        fetchMarkets();
      }
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  // Apply filters when status, category, or search filters change
  useEffect(() => {
    if (!Array.isArray(markets)) {
      logger.error('Markets is not an array:', markets);
      setFilteredMarkets([]);
      return;
    }
    
    let result = [...markets];
    
    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(market => 
        market.title.toLowerCase().includes(term) || 
        market.description.toLowerCase().includes(term)
      );
    }
    
    // Apply status filter using only MarketStatus enum values
    if (statusFilter !== 'all') {
        result = result.filter(market => market.status === statusFilter);
    }
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      result = result.filter(market => market.category === categoryFilter);
    }
    
    setFilteredMarkets(result);
  }, [statusFilter, categoryFilter, searchTerm, markets]);

  // Check if user has admin privileges
  const isUserAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';

  if (!isUserAdmin) {
    navigate('/markets');
    return null;
  }

  if (loading) {
    return (
      <div className="py-8 px-4 md:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">Markets</h1>
        </div>
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal"></div>
          </div>
          <p className="text-center text-gray-500 mt-4">Loading markets data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 px-4 md:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">Markets</h1>
        </div>
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="text-center text-red-600 font-medium">{error}</div>
          <p className="text-center text-gray-500 mt-2">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  // Count markets by status for summary stats - Use only official statuses
  const marketStats = {
    total: markets.length,
    active: markets.filter(m => m.status === 'active').length,
    ended: markets.filter(m => m.status === 'ended').length, // Filter by 'ended' status
    resolved: markets.filter(m => m.status === 'resolved').length,
    expired: markets.filter(m => m.status === 'expired').length, // Add 'expired'
    cancelled: markets.filter(m => m.status === 'cancelled').length,
    pending_approval: markets.filter(m => m.status === 'pending_approval').length, // Add 'pending_approval'
    rejected: markets.filter(m => m.status === 'rejected').length, // Add 'rejected'
    // Removed counts for 'unresolved', 'verified', 'unverified', 'private', 'public'
  };

  return (
    <div className="py-8 px-4 md:px-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold">Markets</h1>
        </div>
        <div className="flex gap-2">
          <Link
            to="/admin/create-market"
            className="flex items-center gap-1 bg-wayja-teal text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors text-sm whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            <span>Create Market</span>
          </Link>
        </div>
      </div>

      {/* Market Stats - Display counts for official statuses */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-6">
        <button 
          onClick={() => setStatusFilter('all')}
          className={`bg-gray-50 rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow ${statusFilter === 'all' ? 'ring-2 ring-gray-400' : ''}`}
        >
          <div className="text-xs text-gray-500">Total</div>
          <div className="text-lg font-bold">{marketStats.total}</div>
        </button>
        <button 
          onClick={() => setStatusFilter('active')}
          className={`bg-green-50 rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow ${statusFilter === 'active' ? 'ring-2 ring-green-400' : ''}`}
        >
          <div className="text-xs text-gray-500">Active</div>
          <div className="text-lg font-bold text-green-700">{marketStats.active}</div>
        </button>
        <button 
          onClick={() => setStatusFilter('ended')}
          className={`bg-wayja-teal/10 rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow ${statusFilter === 'ended' ? 'ring-2 ring-wayja-teal' : ''}`}
        >
          <div className="text-xs text-gray-500">Ended</div>
          <div className="text-lg font-bold text-orange-700">{marketStats.ended}</div>
        </button>
        <button 
          onClick={() => setStatusFilter('resolved')}
          className={`bg-blue-50 rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow ${statusFilter === 'resolved' ? 'ring-2 ring-blue-400' : ''}`}
        >
          <div className="text-xs text-gray-500">Resolved</div>
          <div className="text-lg font-bold text-blue-700">{marketStats.resolved}</div>
        </button>
        <button 
          onClick={() => setStatusFilter('expired')}
          className={`bg-gray-100 rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow ${statusFilter === 'expired' ? 'ring-2 ring-gray-400' : ''}`}
        >
          <div className="text-xs text-gray-500">Expired</div>
          <div className="text-lg font-bold text-gray-700">{marketStats.expired}</div>
        </button>
        <button 
          onClick={() => setStatusFilter('pending_approval')}
          className={`bg-yellow-50 rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow ${statusFilter === 'pending_approval' ? 'ring-2 ring-yellow-400' : ''}`}
        >
          <div className="text-xs text-gray-500">Pending</div>
          <div className="text-lg font-bold text-yellow-700">{marketStats.pending_approval}</div>
        </button>
        <button 
          onClick={() => setStatusFilter('cancelled')}
          className={`bg-red-50 rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow ${statusFilter === 'cancelled' ? 'ring-2 ring-red-400' : ''}`}
        >
          <div className="text-xs text-gray-500">Cancelled</div>
          <div className="text-lg font-bold text-red-700">{marketStats.cancelled}</div>
        </button>
        <button 
          onClick={() => setStatusFilter('rejected')}
          className={`bg-red-100 rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow ${statusFilter === 'rejected' ? 'ring-2 ring-red-400' : ''}`}
        >
          <div className="text-xs text-gray-500">Rejected</div>
          <div className="text-lg font-bold text-red-700">{marketStats.rejected}</div>
        </button>
      </div>
      
      {/* Filter Controls */}
      <div className="mb-6 overflow-hidden bg-white rounded-lg shadow-sm">
        {/* Filter header with title and clear button */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-base font-medium text-gray-800">Market Filters</h2>
          {(statusFilter !== 'all' || categoryFilter !== 'all' || searchTerm.trim()) && (
            <button 
              onClick={() => {
                setStatusFilter('all');
                setCategoryFilter('all');
                setSearchTerm('');
              }}
              className="text-sm text-red-500 hover:text-red-600 flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" />
              Clear all
            </button>
          )}
        </div>
        
        {/* All filters in a single row */}
        <div className="px-4 py-4 flex items-center flex-wrap gap-6">
          {/* Search */}
          <div className="w-72">
            <div className="relative">
              <input
                type="text"
                placeholder="Search markets..."
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-wayja-teal focus:bg-white focus:border-wayja-teal text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>
          
          {/* Category Filter */}
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-gray-700">Category:</div>
            <div className="relative">
              <button
                ref={categoryButtonRef}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium min-w-[160px] justify-between"
                onClick={(e) => {
                  e.stopPropagation();
                  updateDropdownPosition();
                  setShowCategoryDropdown(!showCategoryDropdown);
                }}
              >
                <span className="truncate">{categoryFilter === 'all' ? 'All Categories' : categoryFilter}</span>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Results count */}
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-sm text-gray-500">
          Showing {filteredMarkets.length} of {Array.isArray(markets) ? markets.length : 0} markets
        </div>
      </div>
      
      {/* Markets Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.isArray(filteredMarkets) && filteredMarkets.length > 0 ? (
          filteredMarkets.map(market => {
            // Log market rendering at debug level
            logger.debug(`Rendering market ${market.id}:`, {
              title: market.title,
              status: market.status,
              isPrivate: market.is_private,
              isVerified: market.is_verified,
              hasOptions: !!market.market_options?.length
            });

            return (
              <div key={market.id} className="hover:shadow-lg transition-shadow">
                <MarketCard market={market} isAdmin={true} />
              </div>
            );
          })
        ) : (
          <div className="col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4 bg-white rounded-lg p-6 text-center shadow-sm">
            <p className="text-gray-600">No markets found with the selected filters.</p>
          </div>
        )}
      </div>

      {/* Category Dropdown - Rendered using state */}
      {showCategoryDropdown && (
        <div 
          ref={categoryDropdownRef}
          className="fixed rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-40"
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${Math.max(dropdownPosition.width, 200)}px`,
            maxHeight: '300px',
            overflowY: 'auto'
          }}
        >
          <ul className="py-1" role="menu">
            {/* Add 'All Categories' option first */} 
            <li>
              <button
                type="button"
                className={`block w-full text-left px-4 py-2 text-sm ${
                  categoryFilter === 'all' ? 'font-medium' : 'text-gray-700 hover:bg-gray-100'
                }`}
                style={categoryFilter === 'all' ? { backgroundColor: '#5cb3ad20', color: '#5cb3ad' } : undefined}
                onClick={() => {
                  setCategoryFilter('all');
                  setShowCategoryDropdown(false);
                }}
              >
                All Categories
              </button>
            </li>
            {/* Map over fetched categories */} 
            {categories.map((category) => (
              <li key={category.id}>
                <button
                  type="button"
                  className={`block w-full text-left px-4 py-2 text-sm ${
                    categoryFilter === category.name ? 'font-medium' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  style={categoryFilter === category.name ? { backgroundColor: '#5cb3ad20', color: '#5cb3ad' } : undefined}
                  onClick={() => {
                    setCategoryFilter(category.name);
                    setShowCategoryDropdown(false);
                  }}
                >
                  {category.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}